import { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/register", label: "Registration" },
  { to: "/cheer", label: "Cheer" },
  { to: "/calendar", label: "Calendar" },
  { to: "/store", label: "Store" },
  { to: "/forms", label: "Forms" },
  { to: "/coaches", label: "Coaches" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header className="sticky top-0 z-50" data-testid="site-header">
      {/* Quick Info Marquee Banner */}
      <div className="bg-[#2D5F3F] text-[#FDFBF7] overflow-hidden border-b border-black/20" data-testid="quick-info-banner">
        <div className="flex whitespace-nowrap py-2 marquee text-xs sm:text-sm font-bold uppercase tracking-[0.2em]">
          {[0, 1].map((k) => (
            <div key={k} className="flex shrink-0 items-center gap-10 px-6">
              <span className="text-[#FBBF24]">★ All Payments Due Friday · Aug 14</span>
              <span>★ Youth Tackle Football · Ages 6 – 13</span>
              <span>★ All-In Fee $100 · Uniform Included</span>
              <span>★ Mount Baldy Conference</span>
              <span>★ Practice Begins July 20, 2026</span>
              <span>★ Coach &amp; Board Spots Open</span>
              <span>★ Cawstontitans@gmail.com</span>
            </div>
          ))}
        </div>
      </div>

      {/* Main Nav */}
      <nav className="bg-[#F5EFE0]/90 backdrop-blur-xl border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
            <div className="w-11 h-11 bg-[#2D5F3F] flex items-center justify-center shadow-ink-offset transition-transform group-hover:-translate-y-0.5 overflow-hidden">
              <img src="/assets/titans-warrior-logo.png" alt="Cawston Titans" className="w-9 h-9 object-contain" />
            </div>
            <div className="flex flex-col leading-none">
              <span className="font-display text-xl md:text-2xl font-black uppercase tracking-tight text-[#0A0A0A]">Cawston</span>
              <span className="font-display text-xs uppercase tracking-[0.3em] text-[#2D5F3F] font-bold">Titans</span>
            </div>
          </Link>

          {/* Desktop */}
          <ul className="hidden lg:flex items-center gap-1">
            {links.map((l) => (
              <li key={l.to}>
                <NavLink
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                  className={({ isActive }) =>
                    `font-display uppercase tracking-wider text-sm font-bold px-3 py-2 transition-colors ${
                      isActive ? "text-[#2D5F3F]" : "text-[#0A0A0A] hover:text-[#2D5F3F]"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              </li>
            ))}
            <li>
              <Link
                to="/register"
                data-testid="nav-cta-secure-spot"
                className="ml-3 inline-flex items-center bg-[#2D5F3F] hover:bg-[#1B4332] text-[#FDFBF7] px-5 py-2.5 font-display uppercase tracking-wider text-sm font-extrabold transition-colors"
              >
                Secure Your Spot
              </Link>
            </li>
          </ul>

          {/* Mobile toggle */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-[#0A0A0A] p-2"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="lg:hidden border-t border-black/10 bg-[#FAF6EC]" data-testid="mobile-menu">
            <ul className="flex flex-col px-6 py-4 gap-1">
              {links.map((l) => (
                <li key={l.to}>
                  <NavLink
                    to={l.to}
                    end={l.to === "/"}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                    className={({ isActive }) =>
                      `block font-display uppercase tracking-wider text-base font-bold py-3 border-b border-black/10 ${
                        isActive ? "text-[#2D5F3F]" : "text-[#0A0A0A]"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </li>
              ))}
              <li className="pt-4">
                <Link
                  to="/register"
                  data-testid="mobile-nav-cta"
                  className="block text-center bg-[#2D5F3F] text-[#FDFBF7] px-5 py-3 font-display uppercase tracking-wider font-extrabold"
                >
                  Secure Your Spot
                </Link>
              </li>
            </ul>
          </div>
        )}
      </nav>
    </header>
  );
}
